import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { useTournamentEdit, TournamentEditInput } from "@/graphql/hooks/useTournamentEdit"
import { useGameList } from "@/graphql/hooks/useGameList"
import { useTournamentTypesList } from "@/graphql/hooks/useTournamentTypesList"
import { useAllFormats } from "@/graphql/hooks/useAllFormats"
import { useSeasonsByGame } from "@/graphql/hooks/useSeasons"
import { Tournament } from "@/graphql/types"
import { CheckCheck, Link2 } from "lucide-react"
import { useAuthContext } from "@/contexts/AuthContext"
import { TournamentRelatedSelector } from "./TournamentRelatedSelector"

interface RelatedTournament {
  id: number
  name: string
  startDate?: string
  quotas?: number
}

interface TournamentWithRelateds extends Tournament {
  TournamentRelateds?: Array<{
    id: number
    quotas: number
    tournamentId: number
    relatedId: number
    Tournament?: {
      name: string
      startDate: string
    }
  }>
}

const formSchema = z.object({
  name: z.string().min(1, "El nombre es requerido"),
  description: z.string().min(1, "La descripción es requerida"),
  gameId: z.number().min(1, "Debe seleccionar un juego"),
  typeId: z.number().min(1, "Debe seleccionar un tipo de torneo"),
  formatId: z.number().min(1, "Debe seleccionar un formato"),
  seasonId: z.number().min(1, "La temporada es requerida"),
  // maxPlayers: z.number().min(2, "Mínimo 2 jugadores"),
  startDate: z.string().min(1, "La fecha es requerida"),
  // validDates: z.number(),
  private: z.number(),
  eventUrl: z.string().optional(),
  image: z.string().optional(),
})

type FormData = z.infer<typeof formSchema>

interface TournamentEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tournament: TournamentWithRelateds
  related: Tournament[]
  onSuccess?: () => void
}

export const TournamentEditModal = ({
  open,
  onOpenChange,
  tournament,
  related,
  onSuccess,
}: TournamentEditModalProps) => {
  const [selectedGameId, setSelectedGameId] = useState<number>(tournament.gameId)
  const [relatedTournaments, setRelatedTournaments] = useState<RelatedTournament[]>([])
  const { isAdmin } = useAuthContext()
  const { editTournament, loading: editLoading } = useTournamentEdit()
  const { games, loading: gamesLoading } = useGameList({
    storeId: tournament.storeId,
  })
  const { tournamentTypes, loading: typesLoading } = useTournamentTypesList({
    storeId: tournament.storeId,
  })
  const { formats, loading: formatsLoading } = useAllFormats()
  const { seasons, loading: seasonsLoading } = useSeasonsByGame(selectedGameId)

  // Initialize related tournaments from tournament data
  useEffect(() => {
    if (related && related.length > 0) {
      const initialRelateds = related.map((related) => ({
        id: related.id,
        name: related.name || `Torneo #${related.id}`,
        selected: related.parentId === tournament.id, // Mark as active if it's currently related
      }))
      setRelatedTournaments(initialRelateds)
    }
  }, [related, tournament.id])

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: tournament.name,
      description: tournament.description || "",
      gameId: tournament.gameId,
      typeId: tournament.typeId,
      formatId: tournament.formatId,
      seasonId: tournament.seasonId || 0,
      startDate: tournament.startDate,
      private: tournament.isPrivate ? 1 : 0,
      eventUrl: tournament.eventUrl || "",
      image: tournament.image || "",
    },
  })

  // Filter formats based on selected game
  const availableFormats = formats.filter((format) =>
    format.FormatGames.some((fg) => fg.gameId === selectedGameId),
  )

  const onSubmit = async (data: FormData) => {
    try {
      const input: TournamentEditInput = {
        id: tournament.id,
        name: data.name,
        description: data.description,
        gameId: data.gameId,
        typeId: data.typeId,
        formatId: data.formatId,
        seasonId: Number(data.seasonId),
        startDate: `${data.startDate} 00:00`,
        storeId: tournament.storeId,
        // validDates: data.validDates,
        eventUrl: data.eventUrl,
        private: data.private || 0,
        image: data.image,
        addressId: tournament.addressId,
        // relateds: relatedTournaments.map((t) => ({ relatedId: t.id, quotas: 0 })),
      }

      const result = await editTournament(input)
      if (result?.status?.success) {
        onOpenChange(false)
        onSuccess?.()
      }
    } catch (error) {
      console.error("Error editing tournament:", error)
    }
  }

  const formatDate = (date: string) => {
    if (!date) return ""
    return new Date(date).toISOString().slice(0, 10)
  }

  useEffect(() => {
    if (tournament.startDate) {
      form.setValue("startDate", formatDate(tournament.startDate))
    }
  }, [tournament.startDate, form])

  const loading = gamesLoading || typesLoading || formatsLoading || seasonsLoading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
        <DialogHeader>
          <DialogTitle>Editar Torneo</DialogTitle>
          <DialogDescription>
            Modifica los detalles del torneo. Los campos marcados con * son obligatorios.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <LoadingScreen message="Cargando datos..." />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
                      <FormLabel>Nombre del Torneo *</FormLabel>
                      <FormControl>
                        <Input
                          className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600"
                          placeholder="Nombre del torneo"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gameId"
                  render={({ field }) => (
                    <FormItem className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
                      <FormLabel>Formato *</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          const gameId = parseInt(value)
                          field.onChange(gameId)
                          setSelectedGameId(gameId)
                          form.setValue("formatId", 0)
                        }}
                        value={field.value?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600">
                            <SelectValue placeholder="Selecciona un juego" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {games.map((game) => (
                            <SelectItem key={game.id} value={game.id.toString()}>
                              {game.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
                    <FormLabel>Descripción *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe tu torneo..."
                        className="min-h-[100px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="typeId"
                  render={({ field }) => (
                    <FormItem className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
                      <FormLabel>Tipo de Torneo *</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(parseInt(value))}
                        value={field.value?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600">
                            <SelectValue placeholder="Selecciona el tipo" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {tournamentTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id.toString()}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="formatId"
                  render={({ field }) => (
                    <FormItem className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
                      <FormLabel>Modalidad *</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(parseInt(value))}
                        value={field.value?.toString()}
                        disabled={!selectedGameId}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600">
                            <SelectValue placeholder="Selecciona el formato" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableFormats.map((format) => (
                            <SelectItem key={format.id} value={format.id.toString()}>
                              {format.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {isAdmin && (
                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="seasonId"
                    render={({ field }) => (
                      <FormItem className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
                        <FormLabel>Temporada</FormLabel>
                        <Select
                          onValueChange={(value) =>
                            field.onChange(value ? Number(value) : undefined)
                          }
                          value={field.value?.toString() || ""}
                        >
                          <FormControl>
                            <SelectTrigger className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600">
                              <SelectValue placeholder="Selecciona temporada (opcional)" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="0">Sin temporada</SelectItem>
                            {seasons
                              .filter((m) => m)
                              .map((season) => (
                                <SelectItem
                                  key={season.id}
                                  value={season.id.toString()}
                                  disabled={!season.isActive && !isAdmin}
                                >
                                  {season.name}{" "}
                                  {season.isActive ? (
                                    <span>
                                      <CheckCheck className="h-4 inline text-primary" />
                                      Activa
                                    </span>
                                  ) : (
                                    ""
                                  )}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
                      <FormLabel>Fecha y Hora de Inicio *</FormLabel>
                      <FormControl>
                        <Input
                          className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600"
                          type="date"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="eventUrl"
                render={({ field }) => (
                  <FormItem className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
                    <div className="space-y-0.5 grid">
                      <FormLabel>URL del Evento</FormLabel>
                      <span className="ml-2 sm:ml-2 text-xs">
                        La URL se mostrará en el listado de eventos
                      </span>
                    </div>
                    <FormControl>
                      <Input
                        className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600"
                        type="url"
                        placeholder="https://instagram.com/"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="private"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5 grid">
                        <FormLabel className="text-md w-full">¿Torneo Privado?</FormLabel>
                        <span className="ml-2 sm:ml-2 text-xs">
                          Al ser privado no se listará en el sistema, pero cuando lo finalices
                          quedará público automáticamente.
                        </span>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value === 1}
                          onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                          className="border-gray-200 dark:border-gray-600 bg-white-100 dark:bg-primary-700"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {/* Related Tournaments Section */}
              {isAdmin && (
                <div className="relative overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                  {/* Header */}
                  <div className="flex items-start gap-3 border-b border-border px-6 py-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Link2 className="h-5 w-5 text-primary" />
                    </div>

                    <div className="flex flex-col">
                      <Label className="text-base font-semibold tracking-tight">
                        Torneos Relacionados
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Asocia torneos a este evento. Los jugadores que clasifiquen podrán
                        inscribirse automáticamente.
                      </p>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="space-y-4 px-6 py-5">
                    <div className="rounded-lg border border-dashed border-amber-400/40 bg-amber-500/5 p-4">
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        🚧 Sección en desarrollo.
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Próximamente permitirá asociar torneos por zonas y validar que un usuario no
                        esté inscrito en más de una zona simultáneamente.
                      </p>
                    </div>

                    <TournamentRelatedSelector
                      currentTournamentId={tournament.id}
                      related={related}
                      selectedTournaments={relatedTournaments}
                      onSelectionChange={setRelatedTournaments}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="border border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancelar
                </Button>

                <Button
                  disabled={editLoading}
                  className="bg-primary text-primary-foreground dark:bg-primary-dark dark:text-primary-foreground-dark hover:bg-primary/90 dark:hover:bg-primary-dark/90"
                >
                  {editLoading ? "Actualizando..." : "Actualizar Torneo"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
